import { LOCALES, type Locale } from '../i18n/index.js';

/**
 * Step 0: the very first screen. It speaks every language at once until the
 * player picks one, so its copy lives here per locale instead of in the
 * dictionaries: moving the highlight previews that language live.
 */
const COPY: Record<Locale, { eyebrow: string; title: string; accent: string; lede: string; cta: string; hint: string; sample: string }> = {
  de: {
    eyebrow: 'WILLKOMMEN BEI NIGHTSHIFT',
    title: 'WÄHLE DEINE',
    accent: 'SPRACHE',
    lede: 'Menüs, Rennfunk und Hinweise erscheinen in dieser Sprache. Wechseln kannst du jederzeit über das Sprach-Symbol oben.',
    cta: 'LOS GEHT’S',
    hint: '← → wählen · Enter ↵ bestätigen',
    sample: 'Die Nacht gehört dir.',
  },
  en: {
    eyebrow: 'WELCOME TO NIGHTSHIFT',
    title: 'CHOOSE YOUR',
    accent: 'LANGUAGE',
    lede: 'Menus, race control and hints use this language. Switch any time via the language icon at the top.',
    cta: 'LET’S GO',
    hint: '← → choose · Enter ↵ confirm',
    sample: 'The night is yours.',
  },
};

export function languageStepCopy(locale: Locale): (typeof COPY)[Locale] {
  return COPY[locale];
}

export function languageStepHtml(selected: Locale): string {
  const copy = COPY[selected];
  return `
    <div class="language-step" data-selected="${selected}">
      <div class="language-intro">
        <p class="eyebrow" data-lang-copy="eyebrow">${copy.eyebrow}</p>
        <h1 id="language-title"><span data-lang-copy="title">${copy.title}</span><br><em data-lang-copy="accent">${copy.accent}</em></h1>
        <p class="wizard-lede" data-lang-copy="lede">${copy.lede}</p>
      </div>
      <div class="language-cards" role="radiogroup" aria-labelledby="language-title">
        ${LOCALES.map((option, index) => `
          <button type="button" role="radio" class="language-card" data-language="${option.id}" aria-checked="${option.id === selected}" lang="${option.id}" style="--card-index:${index}" ${option.id === selected ? 'data-pad-default' : ''}>
            <span class="language-card-glow" aria-hidden="true"></span>
            <span class="language-card-code" aria-hidden="true">${option.id.toUpperCase()}</span>
            <span class="language-card-body">
              <b>${option.native}</b>
              <small>${option.region}</small>
              <em>“${COPY[option.id].sample}”</em>
            </span>
            <span class="language-card-check" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></span>
          </button>`).join('')}
      </div>
    </div>`;
}

/** Repaints the bilingual copy and footer for the highlighted language. */
export function paintLanguageStep(root: HTMLElement, selected: Locale): void {
  const copy = COPY[selected];
  const step = root.querySelector<HTMLElement>('.language-step');
  if (step) step.dataset.selected = selected;
  for (const element of root.querySelectorAll<HTMLElement>('[data-lang-copy]')) {
    const key = element.dataset.langCopy as keyof typeof copy;
    if (element.textContent !== copy[key]) {
      element.textContent = copy[key];
      element.classList.remove('lang-swap');
      void element.offsetWidth;
      element.classList.add('lang-swap');
    }
  }
  for (const card of root.querySelectorAll<HTMLElement>('[data-language]')) {
    const active = card.dataset.language === selected;
    card.setAttribute('aria-checked', String(active));
    card.toggleAttribute('data-pad-default', active);
  }
  const next = root.querySelector<HTMLElement>('[data-wizard-action="language-next"]');
  if (next?.firstChild) next.firstChild.textContent = copy.cta;
  const hint = root.querySelector<HTMLElement>('.wizard-hint');
  if (hint) hint.textContent = copy.hint;
}

export interface LanguageStepHost {
  select(locale: Locale): void;
  confirm(locale: Locale): void;
}

/** Wires the cards: click/tap picks and continues, arrows/focus preview a language. */
export function bindLanguageStep(root: HTMLElement, initial: Locale, host: LanguageStepHost): () => void {
  let selected = initial;
  let confirmed = false;
  // Enter on a focused card fires both keydown and click; continue once.
  const confirm = (locale: Locale): void => {
    if (confirmed) return;
    confirmed = true;
    host.confirm(locale);
  };
  const choose = (locale: Locale): void => {
    selected = locale;
    paintLanguageStep(root, locale);
    host.select(locale);
  };
  const cards = [...root.querySelectorAll<HTMLButtonElement>('[data-language]')];
  for (const card of cards) {
    const locale = card.dataset.language as Locale;
    // One click picks and continues; the short beat lets the check land.
    card.addEventListener('click', () => {
      if (selected !== locale) choose(locale);
      root.querySelector('.language-step')?.classList.add('confirming');
      window.setTimeout(() => confirm(locale), 240);
    });
    card.addEventListener('focus', () => {
      if (selected !== locale) choose(locale);
    });
  }
  root.querySelector('[data-wizard-action="language-next"]')?.addEventListener('click', () => confirm(selected));
  const onKey = (event: KeyboardEvent): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const index = LOCALES.findIndex((option) => option.id === selected);
    if (['ArrowLeft', 'ArrowUp', 'KeyA', 'KeyW'].includes(event.code)) {
      event.preventDefault();
      const target = LOCALES[(index + LOCALES.length - 1) % LOCALES.length]!.id;
      choose(target);
      root.querySelector<HTMLElement>(`[data-language="${target}"]`)?.focus({ preventScroll: true });
    } else if (['ArrowRight', 'ArrowDown', 'KeyD', 'KeyS'].includes(event.code)) {
      event.preventDefault();
      const target = LOCALES[(index + 1) % LOCALES.length]!.id;
      choose(target);
      root.querySelector<HTMLElement>(`[data-language="${target}"]`)?.focus({ preventScroll: true });
    } else if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      const focused = document.activeElement as HTMLElement | null;
      if (focused?.matches('button') && !focused.matches('[data-language], [data-wizard-action="language-next"]')) return;
      event.preventDefault();
      confirm(selected);
    }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}
